import { Loader2, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useRadio } from "@/context/RadioContext";
import { ShareNowPlaying } from "@/components/site/ShareNowPlaying";
import { FavoriteButton } from "@/components/site/FavoriteButton";

const Equalizer = ({ active }) => (
  <div className={`flex items-end gap-[3px] h-5 ${active ? "eq" : ""}`} aria-hidden>
    {[0, 0.2, 0.4, 0.1].map((d, i) => (
      <span key={i} className="w-[3px] h-full bg-primary" style={{ animationDelay: `${d}s`, transform: active ? undefined : "scaleY(0.3)" }} />
    ))}
  </div>
);

const NowCover = ({ cover, playing }) => (
  <div className="relative w-12 h-12 shrink-0 bg-[hsl(var(--surface))] overflow-hidden" data-testid="radio-now-cover">
    <img src={cover || "/brand/logo.png"} alt="" className={`w-full h-full ${cover ? "object-cover" : "object-contain p-1.5"}`} data-testid="radio-now-cover-img" />
    <div className="absolute bottom-1 left-1 bg-black/70 px-1 py-0.5"><Equalizer active={playing} /></div>
  </div>
);

export const RadioPlayer = () => {
  const { playing, loading, toggle, volume, setVolume, nowPlaying, cover, current } = useRadio();
  return (
    <div className="fixed bottom-0 inset-x-0 z-[100] bg-black border-t border-white/10" data-testid="radio-player">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-20 flex items-center gap-5">
        <button
          onClick={toggle}
          data-testid="radio-play-toggle"
          aria-label={playing ? "Pause" : "Écouter la radio"}
          className="w-12 h-12 shrink-0 bg-primary text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors duration-200"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <NowCover cover={cover} playing={playing} />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary items-center gap-2 whitespace-nowrap hidden sm:flex">
            <span className="w-1.5 h-1.5 bg-primary inline-block" /> Radio LGTDZ · En continu
          </p>
          <p className="text-white truncate text-sm md:text-base" data-testid="radio-now-playing">
            {nowPlaying || "LGTDZ — le média, la radio"}
          </p>
        </div>
        <FavoriteButton track={current} testId="radio-favorite-btn" className="w-11 h-11" />
        <ShareNowPlaying
          testId="radio-share"
          label={<span className="hidden md:inline text-[11px] uppercase tracking-[0.2em] font-bold">Partager</span>}
          className="h-11 w-11 md:w-auto md:px-3 shrink-0 border border-white/20 text-white hover:border-primary hover:text-primary"
        />
        <div className="hidden md:flex items-center gap-3 w-44">
          <button onClick={() => setVolume(volume > 0 ? 0 : 0.8)} data-testid="radio-mute-toggle" aria-label="Volume" className="text-zinc-400 hover:text-white transition-colors">
            {volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <Slider value={[volume * 100]} max={100} step={1} onValueChange={(v) => setVolume(v[0] / 100)} data-testid="radio-volume-slider" />
        </div>
      </div>
    </div>
  );
};
